// Действия над готовым результатом. Экраны 06, 07, 08.

import type { Artifact, ArtifactType, ScenarioId, UnitId } from '@smyslika/core'

import type { Client } from './client.ts'

/**
 * Готовый результат, открытый из истории. Экраны 06–08 живут на активном
 * запуске, а он в памяти сервера и пропадает вместе с ней; строка истории
 * открывает тот же артефакт этой ручкой.
 */
export interface StoredArtifact extends Artifact {
  type: ArtifactType
  scenario: ScenarioId
  unit: UnitId
  /** Списалась ли единица за этот результат. */
  charged: boolean
  finishedAt?: string
  /** Запуск, который его выдал: по нему открываются детали результата. */
  runId: string
}

export const EXPORT_FORMATS = ['docx', 'pdf'] as const
export type ExportFormat = (typeof EXPORT_FORMATS)[number]

export interface Exported {
  fileId: string
  filename: string
  url: string
}

/**
 * Ответ на правку. Ручка отвечает одно из трёх и никогда не решает молча:
 * запускает бесплатную правку; говорит, что это новая задача — и тогда клиент
 * идёт обычным путём, через подтверждение; или что это вопрос о результате —
 * и тогда обычным путём тоже, но там его подхватит бесплатное уточнение.
 *
 * Вопрос отделён от новой задачи, хотя клиент в обоих случаях делает одно и то
 * же: у них разное списание, и разница видна человеку. Свести их в один ответ
 * значило бы потерять её в первом же экране, который захочет о ней сказать.
 */
export type EditResult =
  | { kind: 'free'; runId: string }
  | { kind: 'new_task'; reason: string; message: string }
  | { kind: 'question' }

/**
 * Что делать с ответом на правку — одно решение на отправку и на её досылку
 * после обрыва (SMY-449; ворота ревью 06.10.2026, dedup-hunter). `adopt` —
 * правка заведена, подхватить её запуск; `reload` — запас уточнений кончился, а
 * экран не знал: перечитать ленту и оставить текст в поле, иначе текст ушёл бы
 * бесплатным уточнением, которое сервер спишет без подтверждения (SMY-336);
 * `continue` — вопрос о результате или новая задача: дальше обычным путём.
 */
export type EditNext = { next: 'adopt'; runId: string } | { next: 'reload' } | { next: 'continue' }

export function afterEdit(res: EditResult): EditNext {
  if (res.kind === 'free') return { next: 'adopt', runId: res.runId }
  if (res.kind === 'new_task' && res.reason === 'followups_spent') return { next: 'reload' }
  return { next: 'continue' }
}

export const createArtifacts = (client: Client) => ({
  read: (id: string) => client.request<StoredArtifact>(`/v1/artifacts/${encodeURIComponent(id)}`),

  export: (id: string, format: ExportFormat = 'docx') =>
    client.request<Exported>(`/v1/artifacts/${encodeURIComponent(id)}/export`, {
      method: 'POST',
      body: { format },
    }),

  /**
   * Правка результата. `key` — ключ попытки (SMY-449): повтор с тем же ключом
   * получает тот же ответ, и вторая правка из запаса не тратится.
   * `assumptionOf` — правка начата кнопкой «Изменить» под ответом этого
   * запуска (SMY-497): только для счёта, бесплатность от него не зависит.
   */
  async edit(id: string, text: string, key?: string, signal?: AbortSignal, assumptionOf?: string): Promise<EditResult> {
    const res = await client.request<Record<string, unknown>>(
      `/v1/artifacts/${encodeURIComponent(id)}/edit`,
      {
        method: 'POST',
        body: { text, ...(key != null ? { key } : {}), ...(assumptionOf != null ? { assumptionOf } : {}) },
        ...(signal != null ? { signal } : {}),
      },
    )
    if (typeof res.runId === 'string') return { kind: 'free', runId: res.runId }
    if (res.followUp === 'question') return { kind: 'question' }
    return {
      kind: 'new_task',
      reason: typeof res.reason === 'string' ? res.reason : 'new_task',
      message: typeof res.message === 'string' ? res.message : 'Это новая задача — она спишется отдельно.',
    }
  },
})

export type Artifacts = ReturnType<typeof createArtifacts>
